import { navigate } from 'gatsby'
import {
  call,
  put,
  select,
  takeEvery,
  take,
  race,
  spawn,
  delay,
} from 'redux-saga/effects'

import { installApp } from '@/store/installedApps'
import { requestUninstallApp } from '@/store/installedApps/sagas'
import { startPayment, setCreatedChargeInfo } from '@/store/payment'
import { notificationsCreate } from '@/store/notifications'
import {
  readRecurrentCharge,
  createRecurrentCharge,
  acceptRecurrentCharge,
  cancelRecurrentCharge,
  declineRecurrentCharge,
  activateRecurrentCharge,
  recurrentChargePollStart,
  recurrentChargePollStop,
  archiveRecurrentCharge,
} from '@/store/recurrentCharges'

import appsApi from '@/apis/apps'
import recurrentChargeApi from '@/apis/billing/recurrentCharge'
import taxesApi from '@/apis/billing/taxes'
import { getCartAgentsCountForProduct, getItemById } from '@/store/selectors'
import {
  RECURRENT_CHARGES,
  PAYMENT,
  SENTRY,
  NOTIFICATIONS,
  EVENT_SOURCE_IDS,
  ToastKind,
} from '@/constants'
import { OK, CREATED, NOT_FOUND } from '@/constants/utils'
import { errorHandler, SagaError } from '@/utils/sagas'
import { calculateChargePrice, getPaymentPlanId } from '@/utils/products'
import { sendPostMessage } from '@/utils/postMessage/utils'
import { PM_EVENT } from '@/utils/postMessage/constants'
import { customNavigate, isPublicVersion } from '@/utils'

export function* requestRecurrentChargeRead({
  payload: { chargeId, productType },
}) {
  try {
    const { status, json } = yield call(
      recurrentChargeApi.read,
      chargeId,
      productType
    )

    if (status === OK) {
      const searchParams = new URLSearchParams(window.location.search)
      const icon = searchParams.get('icon')
      const description = searchParams.get('description')

      const agentsCount = yield select(
        getCartAgentsCountForProduct,
        productType
      )

      const { baseTotalPrice } = calculateChargePrice({
        charge: json,
        agentsCount,
      })

      const chargeTaxes = yield call(taxesApi.getPricePreview, {
        price: baseTotalPrice,
        productType,
      })

      const intent = {
        name: json.name,
        price: json.price,
        return_url: json.return_url,
        trial_days: json?.trial_days,
        months: json?.months,
        quantity: json?.quantity,
        metadata: {
          icon,
          description,
          type: PAYMENT.RECURRENT_CHARGE_TYPE,
          product: productType,
        },
      }

      yield put(readRecurrentCharge.fulfilled({ item: json }))
      yield put(
        setCreatedChargeInfo({
          intent,
          charge: {
            taxes: chargeTaxes?.json || null,
            ...json,
          },
          chargeType: PAYMENT.RECURRENT_CHARGE,
          isExternalCharge: true,
          isProcessable: json.status === RECURRENT_CHARGES.STATUS.PENDING,
        })
      )
    } else if (status === NOT_FOUND) {
      yield put(
        notificationsCreate({
          kind: ToastKind.Error,
          content: PAYMENT.ERRORS.NOT_FOUND,
          autoHideDelayTime: NOTIFICATIONS.AUTO_HIDE_DELAY_TIME,
          type: isPublicVersion && NOTIFICATIONS.TYPES.PAGE_LOAD,
        })
      )

      yield call(customNavigate, '/')
      throw new SagaError('Recurrent charge not found', json)
    } else {
      throw new SagaError('Recurrent charge fetch failed', json)
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
    yield put(readRecurrentCharge.rejected({ error: error?.message }))
  }
}

export function* requestRecurrentChargeCreate({ payload: { paymentIntent } }) {
  try {
    const { metadata, ...intent } = paymentIntent
    const item = yield select(getItemById, metadata.itemId)
    const planId = getPaymentPlanId(intent)

    const { status, json } = yield call(
      appsApi.createAppCharge,
      metadata.itemId,
      planId
    )

    if (status === CREATED || status === OK) {
      const charge = { appId: metadata.itemId, ...json }

      const agentsCount = yield select(
        getCartAgentsCountForProduct,
        metadata.product
      )

      const { baseTotalPrice } = calculateChargePrice({
        charge,
        agentsCount,
      })

      const chargeTaxes = yield call(taxesApi.getPricePreview, {
        price: baseTotalPrice,
        productType: metadata.product,
      })

      yield put(createRecurrentCharge.fulfilled({ item: charge }))
      yield put(
        setCreatedChargeInfo({
          item,
          intent: paymentIntent,
          charge: {
            ...charge,
            taxes: chargeTaxes?.json || null,
          },
          chargeType: PAYMENT.RECURRENT_CHARGE,
          isExternalCharge: false,
          isProcessable: charge.status === RECURRENT_CHARGES.STATUS.PENDING,
        })
      )
    } else {
      throw new SagaError('Recurrent charge creation failed', json, status)
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
    yield put(createRecurrentCharge.rejected({ error: error?.message }))
    yield put(startPayment.rejected({ error: PAYMENT.ERRORS.BILLING_API }))
  }
}

export function* requestRecurrentChargeActivate({ payload }) {
  try {
    const { paymentId, appId, productType } = payload

    const { status, json } = yield call(
      recurrentChargeApi.activate,
      paymentId,
      productType
    )

    if (status === OK) {
      yield put(
        installApp.fulfilled({
          id: appId,
          sourceId: EVENT_SOURCE_IDS.RECURRENT_CHARGE,
        })
      )

      yield put(
        activateRecurrentCharge.fulfilled({
          item: {
            appId,
            ...json,
          },
        })
      )

      yield put(recurrentChargePollStart(payload))
    } else {
      throw new SagaError(
        `Recurrent charge couldn't be activated`,
        json,
        status
      )
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
    yield put(startPayment.rejected({ error: PAYMENT.ERRORS.BILLING_API }))

    yield put(
      activateRecurrentCharge.rejected({
        error: error?.message,
      })
    )
  }
}

function* recurrentChargeStatusPolling({
  paymentId,
  appId,
  slug,
  productType,
  isExternalCharge,
}) {
  while (true) {
    try {
      const { json, status } = yield call(
        recurrentChargeApi.read,
        paymentId,
        productType
      )

      const linkToThankYou = !isExternalCharge
        ? `/checkout/${slug}/thank-you`
        : `/checkout/charge/${json?.id}/recurrent_charge/thank-you`

      switch (json?.status) {
        case RECURRENT_CHARGES.STATUS.ACTIVE:
          sendPostMessage(PM_EVENT.APP_PURCHASED, {
            app_id: appId,
          })

          if (window.location.pathname.includes('/checkout/')) {
            yield call(navigate, linkToThankYou)
          }

          yield put(startPayment.fulfilled({ json, appId }))
          yield put(archiveRecurrentCharge({ json }))
          yield put(recurrentChargePollStop())
          break

        case RECURRENT_CHARGES.STATUS.DECLINED:
        case RECURRENT_CHARGES.STATUS.CANCELLED:
          yield call(requestUninstallApp, { payload: { id: appId } })
          yield put(
            startPayment.rejected({ error: 'Payment declined/cancelled' })
          )
          throw new SagaError('Recurrent charge failed', json, status)

        default:
          yield put(
            recurrentChargePollStart({
              paymentId,
              appId,
              slug,
              productType,
              isExternalCharge,
            })
          )
      }

      yield delay(2000)
    } catch (error) {
      yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
      yield put(recurrentChargePollStop({ error: error.message }))
    }
  }
}

export function* requestRecurrentChargeCancel({
  payload: { clientId, appId },
}) {
  try {
    const { status, json } = yield call(
      recurrentChargeApi.readAll,
      clientId,
      RECURRENT_CHARGES.STATUS.ACTIVE
    )

    if (status === OK) {
      if (json.result.length) {
        const response = yield call(
          recurrentChargeApi.cancel,
          json.result[0].id
        )

        if (response.status === OK) {
          yield put(
            cancelRecurrentCharge.fulfilled({
              item: {
                appId,
                ...response.json,
              },
            })
          )
        } else {
          throw new SagaError(
            `Recurrent charge couldn't be cancelled`,
            response.json,
            response.status
          )
        }
      }
    } else {
      throw new SagaError(
        `Recurrent charge couldn't be cancelled`,
        json,
        status
      )
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
    yield put(
      cancelRecurrentCharge.rejected({
        error: error?.message,
      })
    )
    throw new Error(`Recurrent charge couldn't be cancelled`)
  }
}

export function* requestRecurrentChargeDecline({
  payload: { paymentId, productType, continueLoading },
}) {
  try {
    const { status, json } = yield call(
      recurrentChargeApi.decline,
      paymentId,
      productType
    )

    if (status === OK) {
      yield put(declineRecurrentCharge.fulfilled({ json, continueLoading }))
      yield put(archiveRecurrentCharge({ json }))
    } else {
      throw new SagaError(`Recurrent charge couldn't be declined`, json, status)
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
    yield put(startPayment.rejected({ error: PAYMENT.ERRORS.BILLING_API }))
    yield put(
      declineRecurrentCharge.rejected({
        error: error?.message,
      })
    )
  }
}

export function* requestRecurrentChargeAccept({ payload }) {
  try {
    const { paymentId, appId, productType } = payload

    yield put(startPayment.pending())

    const { status, json } = yield call(
      recurrentChargeApi.accept,
      paymentId,
      productType
    )

    if (status === OK) {
      yield put(
        acceptRecurrentCharge.fulfilled({
          item: {
            appId,
            ...json,
          },
        })
      )
      yield put(activateRecurrentCharge.pending(payload))
    } else {
      throw new SagaError(`Recurrent charge couldn't be accepted`, json, status)
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
    yield put(startPayment.rejected({ error: PAYMENT.ERRORS.BILLING_API }))

    yield put(
      acceptRecurrentCharge.rejected({
        error: error?.message,
      })
    )
  }
}

export function* watchRecurrentChargesRequests() {
  yield takeEvery(readRecurrentCharge.pending, requestRecurrentChargeRead)
  yield takeEvery(createRecurrentCharge.pending, requestRecurrentChargeCreate)
  yield takeEvery(
    activateRecurrentCharge.pending,
    requestRecurrentChargeActivate
  )
  yield takeEvery(cancelRecurrentCharge.pending, requestRecurrentChargeCancel)
  yield takeEvery(acceptRecurrentCharge.pending, requestRecurrentChargeAccept)
  yield takeEvery(declineRecurrentCharge.pending, requestRecurrentChargeDecline)

  while (true) {
    const { payload } = yield take(recurrentChargePollStart)

    yield race([
      call(recurrentChargeStatusPolling, payload),
      take(recurrentChargePollStop),
    ])
  }
}
